'use client';

import { useEffect, type RefObject } from 'react';

/**
 * Walk up from `target` to `boundary` (inclusive) and return the first element
 * that can actually scroll vertically. `null` when nothing under the pointer is
 * scrollable - in that case we leave the event alone.
 */
function findScrollableUnder(
  target: EventTarget | null,
  boundary: HTMLElement,
): HTMLElement | null {
  let el = target instanceof HTMLElement ? target : null;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
    if (el === boundary) break;
    el = el.parentElement;
  }
  return null;
}

/**
 * Restores wheel / trackpad scrolling inside a portaled overlay (the contents of
 * a `PopoverContent` - combobox lists, date-picker year lists, etc.) opened from
 * within a vaul `Drawer` or a scroll-locked dialog.
 *
 * In those hosts the portaled element sits outside the locked subtree and the
 * host cancels native wheel scrolling on it: the scrollbar still drags, but the
 * wheel / two-finger trackpad gesture does nothing. Rather than fight whichever
 * host cancels the event, we bind ONE non-passive `wheel` listener on the
 * overlay root (`boundaryRef`), find the scrollable element under the pointer,
 * and drive its `scrollTop` ourselves - then `preventDefault()` so there's
 * exactly one scroll (no doubling) and no scroll-chaining to the page behind.
 *
 * React's own `onWheel` is passive and can't `preventDefault`, hence the native
 * listener. Pass the overlay's own DOM node so this works regardless of how the
 * inner scroll container is built (e.g. cmdk's internal list element).
 *
 * @param boundaryRef the overlay root (e.g. the `PopoverContent` element)
 * @param enabled     bind only while mounted/open (defaults to true)
 */
export function useWheelScrollFix(
  boundaryRef: RefObject<HTMLElement | null>,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;

    let bound: HTMLElement | null = null;
    let onWheel: ((e: WheelEvent) => void) | null = null;

    const attach = (): void => {
      const boundary = boundaryRef.current;
      if (!boundary || bound === boundary) return;
      if (bound && onWheel) {
        bound.removeEventListener('wheel', onWheel);
      }

      onWheel = (e: WheelEvent): void => {
        const scroller = findScrollableUnder(e.target, boundary);
        if (!scroller) return;
        const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? scroller.clientHeight : 1;
        scroller.scrollTop += e.deltaY * factor;
        e.preventDefault();
        e.stopPropagation();
      };

      boundary.addEventListener('wheel', onWheel, { passive: false });
      bound = boundary;
    };

    attach();
    const frame = requestAnimationFrame(attach);

    return () => {
      cancelAnimationFrame(frame);
      if (bound && onWheel) {
        bound.removeEventListener('wheel', onWheel);
      }
    };
  }, [boundaryRef, enabled]);
}
